import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  BarChart as BarChartIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface SmsAutomation {
  id: string;
  name: string;
  description?: string;
  trigger_type: 'crm_stage' | 'product_purchase' | 'abandoned_cart';
  trigger_config: {
    stage_ids?: string[];
    products?: string[];
  };
  template_id: string;
  platform?: string;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  status: 'active' | 'paused' | 'inactive';
  is_active: boolean;
  created_at: string;
}

interface AutomationStats {
  total: number;
  pending: number;
  executed: number;
  failed: number;
  cancelled: number;
  delivered: number;
  delivery_rate: number;
}

interface ExecutionHistory {
  id: string;
  contact_id: string;
  recipient_phone: string;
  sms_message: string;
  status: string;
  scheduled_at: string;
  executed_at: string;
  sms_delivered_at?: string;
  error_message?: string;
  contacts?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

interface SmsTemplate {
  id: string;
  name: string;
  message: string;
}

interface CrmStage {
  id: string;
  name: string;
  pipeline_id: string;
}

const PLATFORMS = [
  { value: 'hotmart', label: 'Hotmart' },
  { value: 'kirvano', label: 'Kirvano' },
  { value: 'hubla', label: 'Hubla' },
  { value: 'guru', label: 'Guru' },
];

const steps = ['Tipo de Trigger', 'Configuração', 'Template e Delay'];

export default function SmsAutomations() {
  const [automations, setAutomations] = useState<SmsAutomation[]>([]);
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingAutomation, setEditingAutomation] = useState<SmsAutomation | null>(null);
  const [selectedAutomationId, setSelectedAutomationId] = useState<string | null>(null);
  const [history, setHistory] = useState<ExecutionHistory[]>([]);
  const [stats, setStats] = useState<AutomationStats | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'crm_stage' as 'crm_stage' | 'product_purchase' | 'abandoned_cart',
    stage_ids: [] as string[],
    products: [] as string[],
    platform: '' as string,
    template_id: '',
    delay_days: 0,
    delay_hours: 0,
    delay_minutes: 0,
    status: 'active' as 'active' | 'paused' | 'inactive',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [automationsRes, templatesRes, stagesRes] = await Promise.all([
        axios.get(`${getApiUrl()}/sms-automations`),
        axios.get(`${getApiUrl()}/sms-templates`),
        axios.get(`${getApiUrl()}/crm/pipelines`),
      ]);

      setAutomations(automationsRes.data);
      setTemplates(templatesRes.data);

      // Extrair todos os estágios de todos os pipelines
      const allStages: CrmStage[] = [];
      stagesRes.data.data?.forEach((pipeline: any) => {
        pipeline.stages?.forEach((stage: any) => {
          allStages.push({
            id: stage.id,
            name: stage.name,
            pipeline_id: pipeline.id,
          });
        });
      });
      setCrmStages(allStages);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (automation?: SmsAutomation) => {
    if (automation) {
      setEditingAutomation(automation);
      setFormData({
        name: automation.name,
        description: automation.description || '',
        trigger_type: automation.trigger_type,
        stage_ids: automation.trigger_config.stage_ids || [],
        products: automation.trigger_config.products || [],
        platform: automation.platform || '',
        template_id: automation.template_id,
        delay_days: automation.delay_days || 0,
        delay_hours: automation.delay_hours,
        delay_minutes: automation.delay_minutes,
        status: automation.status,
      });
    } else {
      setEditingAutomation(null);
      setFormData({
        name: '',
        description: '',
        trigger_type: 'crm_stage',
        stage_ids: [],
        products: [],
        platform: '',
        template_id: '',
        delay_days: 0,
        delay_hours: 0,
        delay_minutes: 0,
        status: 'active',
      });
    }
    setActiveStep(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAutomation(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.trigger_type) {
        alert('Selecione um tipo de trigger');
        return;
      }
    } else if (activeStep === 1) {
      if (formData.trigger_type === 'crm_stage' && formData.stage_ids.length === 0) {
        alert('Selecione pelo menos um estágio do CRM');
        return;
      }
      if (formData.trigger_type === 'product_purchase') {
        if (formData.products.length === 0) {
          alert('Informe pelo menos um produto');
          return;
        }
        if (!formData.platform) {
          alert('Selecione a plataforma de vendas');
          return;
        }
      }
      if (formData.trigger_type === 'abandoned_cart' && !formData.platform) {
        alert('Selecione a plataforma de vendas');
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.template_id) {
        alert('Preencha todos os campos obrigatórios');
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        trigger_type: formData.trigger_type,
        trigger_config: {
          stage_ids: formData.trigger_type === 'crm_stage' ? formData.stage_ids : undefined,
          products: formData.trigger_type === 'product_purchase' ? formData.products : undefined,
        },
        template_id: formData.template_id,
        platform:
          formData.trigger_type === 'product_purchase' || formData.trigger_type === 'abandoned_cart'
            ? formData.platform
            : undefined,
        delay_days: formData.delay_days,
        delay_hours: formData.delay_hours,
        delay_minutes: formData.delay_minutes,
        status: formData.status,
      };

      if (editingAutomation) {
        await axios.put(`${getApiUrl()}/sms-automations/${editingAutomation.id}`, payload);
      } else {
        await axios.post(`${getApiUrl()}/sms-automations`, payload);
      }

      await loadData();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar automação:', error);
      alert(error.response?.data?.message || 'Erro ao salvar automação');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta automação?')) {
      return;
    }

    try {
      await axios.delete(`${getApiUrl()}/sms-automations/${id}`);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao deletar automação:', error);
      alert(error.response?.data?.message || 'Erro ao deletar automação');
    }
  };

  const handleToggleStatus = async (automation: SmsAutomation) => {
    try {
      const newStatus = automation.status === 'active' ? 'paused' : 'active';
      await axios.put(`${getApiUrl()}/sms-automations/${automation.id}`, {
        status: newStatus,
      });
      await loadData();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleViewHistory = async (automationId: string) => {
    try {
      setSelectedAutomationId(automationId);
      setLoadingHistory(true);
      const response = await axios.get(`${getApiUrl()}/sms-automations/${automationId}/history`);
      setHistory(response.data);
      setOpenHistoryDialog(true);
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      alert(error.response?.data?.message || 'Erro ao carregar histórico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewStats = async (automationId: string) => {
    try {
      setSelectedAutomationId(automationId);
      setLoadingStats(true);
      const response = await axios.get(`${getApiUrl()}/sms-automations/${automationId}/stats`);
      setStats(response.data);
      setOpenStatsDialog(true);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
      alert(error.response?.data?.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoadingStats(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{
        background: '#ffffff',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        p: 4,
        mb: 6,
        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)',
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <MessageIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
            <Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                Automações de SMS
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Configure automações de SMS baseadas em triggers do CRM
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Nova Automação
          </Button>
        </Box>
      </Box>

      {/* Automation Cards */}
      <Grid container spacing={3}>
        {automations.map((automation) => (
          <Grid item xs={12} md={6} lg={4} key={automation.id}>
            <Box sx={{
              background: '#ffffff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              p: 4,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600, mb: 0.5 }}>
                    {automation.name}
                  </Typography>
                  {automation.description && (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {automation.description}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={automation.status === 'active' ? 'Ativa' : 'Pausada'}
                  sx={{
                    backgroundColor: automation.status === 'active' ? '#d1fae5' : '#e5e7eb',
                    color: automation.status === 'active' ? '#065f46' : '#374151',
                    border: automation.status === 'active' ? '1px solid #10b981' : '1px solid #9ca3af',
                    fontWeight: 600,
                  }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                  <strong style={{ color: '#000000' }}>Trigger:</strong>{' '}
                  {automation.trigger_type === 'crm_stage' && 'Estágio do CRM'}
                  {automation.trigger_type === 'product_purchase' && 'Compra de Produto'}
                  {automation.trigger_type === 'abandoned_cart' && 'Carrinho Abandonado'}
                </Typography>
                {automation.platform && (
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                    <strong style={{ color: '#000000' }}>Plataforma:</strong> {automation.platform}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  <strong style={{ color: '#000000' }}>Delay:</strong>{' '}
                  {automation.delay_days > 0 && `${automation.delay_days}d `}
                  {automation.delay_hours > 0 && `${automation.delay_hours}h `}
                  {automation.delay_minutes > 0 && `${automation.delay_minutes}min`}
                  {automation.delay_days === 0 && automation.delay_hours === 0 && automation.delay_minutes === 0 && 'Imediato'}
                </Typography>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Tooltip title="Ver Estatísticas">
                  <IconButton
                    size="small"
                    onClick={() => handleViewStats(automation.id)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <BarChartIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver Histórico">
                  <IconButton
                    size="small"
                    onClick={() => handleViewHistory(automation.id)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={automation.status === 'active' ? 'Pausar' : 'Ativar'}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleStatus(automation)}
                    sx={{
                      color: automation.status === 'active' ? '#f59e0b' : '#10b981',
                      backgroundColor: automation.status === 'active' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      border: automation.status === 'active' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        backgroundColor: automation.status === 'active' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {automation.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(automation)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deletar">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(automation.id)}
                    sx={{
                      color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        ))}

        {automations.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{
              background: '#ffffff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              p: 6,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
            }}>
              <MessageIcon sx={{ color: '#3b82f6', fontSize: 64, mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000', mb: 1, fontWeight: 600 }}>
                Nenhuma automação criada
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                Clique em "Nova Automação" para começar
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Dialog de criação/edição */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          {editingAutomation ? 'Editar Automação' : 'Nova Automação de SMS'}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { color: '#000000' } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Tipo de Trigger */}
          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Nome da Automação"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused': {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000000',
                  },
                }}
              />

              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused': {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000000',
                  },
                }}
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#3b82f6' } }}>Selecionar se campanha é para:</InputLabel>
                <Select
                  value={formData.trigger_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trigger_type: e.target.value as any,
                      stage_ids: [],
                      products: [],
                      platform: '',
                    })
                  }
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    color: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(59, 130, 246, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  }}
                >
                  <MenuItem value="crm_stage">Estágio do CRM</MenuItem>
                  <MenuItem value="product_purchase">Compra de Produto</MenuItem>
                  <MenuItem value="abandoned_cart">Carrinho Abandonado</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Step 2: Configuração do Trigger */}
          {activeStep === 1 && (
            <Box>
              {formData.trigger_type === 'crm_stage' && (
                <Autocomplete
                  multiple
                  options={crmStages}
                  getOptionLabel={(option) => option.name}
                  value={crmStages.filter((s) => formData.stage_ids.includes(s.id))}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      stage_ids: newValue.map((v) => v.id),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Selecionar Estágios" 
                      margin="normal" 
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)',
                          color: '#ffffff',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(59, 130, 246, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  )}
                />
              )}

              {formData.trigger_type === 'product_purchase' && (
                <Box>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#3b82f6' } }}>Escolher qual a plataforma de vendas a integrar</InputLabel>
                    <Select
                      value={formData.platform}
                      onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        color: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                        },
                      }}
                    >
                      {PLATFORMS.map((platform) => (
                        <MenuItem key={platform.value} value={platform.value}>
                          {platform.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Produtos"
                    value={formData.products.join(', ')}
                    onChange={(e) => {
                      const products = e.target.value
                        .split(/[,\n]/)
                        .map((p) => p.trim())
                        .filter((p) => p.length > 0);
                      setFormData({ ...formData, products });
                    }}
                    margin="normal"
                    required
                    multiline
                    rows={4}
                    helperText="Separe os produtos por vírgula ou quebra de linha"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        color: '#ffffff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b82f6',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />
                </Box>
              )}

              {formData.trigger_type === 'abandoned_cart' && (
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Escolher qual a plataforma de vendas a integrar</InputLabel>
                  <Select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      },
                    }}
                  >
                    {PLATFORMS.map((platform) => (
                      <MenuItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {/* Step 3: Template e Delay */}
          {activeStep === 2 && (
            <Box>
              <FormControl fullWidth margin="normal" required>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#3b82f6' } }}>Selecionar Template SMS</InputLabel>
                <Select
                  value={formData.template_id}
                  onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    color: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(59, 130, 246, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  }}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#000000', fontWeight: 600 }}>
                Configurar Delay
              </Typography>
              <Box display="flex" gap={2} mt={1}>
                <TextField
                  label="Dias"
                  type="number"
                  value={formData.delay_days}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_days: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <TextField
                  label="Horas"
                  type="number"
                  value={formData.delay_hours}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_hours: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0, max: 23 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <TextField
                  label="Minutos"
                  type="number"
                  value={formData.delay_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0, max: 59 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.checked ? 'active' : 'paused',
                      })
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3b82f6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                }
                label="Ativar automação"
                sx={{ mt: 2, color: '#000000' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Cancelar
          </Button>
          {activeStep > 0 && (
            <Button 
              onClick={handleBack}
              sx={{
                color: '#64748b',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                }
              }}
            >
              Voltar
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained"
              sx={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              Próximo
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              {editingAutomation ? 'Atualizar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de Estatísticas */}
      <Dialog 
        open={openStatsDialog} 
        onClose={() => setOpenStatsDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          Estatísticas da Automação SMS
        </DialogTitle>
        <DialogContent>
          {loadingStats ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
          ) : stats ? (
            <Box>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {[
                  { label: 'Total', value: stats.total, color: '#3b82f6' },
                  { label: 'Enviados', value: stats.executed, color: '#10b981' },
                  { label: 'Pendentes', value: stats.pending, color: '#f59e0b' },
                  { label: 'Falhas', value: stats.failed, color: '#ef4444' },
                  { label: 'Entregues', value: stats.delivered, color: '#3b82f6' },
                  { label: 'Taxa de Entrega', value: `${stats.delivery_rate.toFixed(1)}%`, color: '#3b82f6' },
                ].map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box sx={{ 
                      background: '#ffffff',
                      border: '2px solid #3b82f6',
                      borderRadius: '12px',
                      textAlign: 'center', 
                      py: 2,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                    }}>
                      <Typography variant="h4" sx={{ color: stat.color, fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Alert 
              severity="info"
              sx={{
                backgroundColor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #3b82f6',
                '& .MuiAlert-icon': {
                  color: '#3b82f6',
                },
              }}
            >
              Nenhuma estatística disponível
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenStatsDialog(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Histórico */}
      <Dialog 
        open={openHistoryDialog} 
        onClose={() => setOpenHistoryDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          Histórico de Execuções SMS
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
          ) : history.length > 0 ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Contato</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Telefone</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Mensagem</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Status</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Agendado</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Executado</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Entregue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((execution) => (
                    <TableRow 
                      key={execution.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#eff6ff',
                        },
                        '& td': {
                          color: '#000000',
                          borderBottom: '1px solid #e5e7eb',
                        },
                      }}
                    >
                      <TableCell>{execution.contacts?.name || '-'}</TableCell>
                      <TableCell>{execution.recipient_phone || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={execution.sms_message || '-'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, color: '#000000' }}>
                            {execution.sms_message || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          sx={{
                            backgroundColor: 
                              execution.status === 'executed' ? '#d1fae5' :
                              execution.status === 'failed' ? '#fee2e2' :
                              execution.status === 'pending' ? '#fef3c7' :
                              '#e5e7eb',
                            color: 
                              execution.status === 'executed' ? '#065f46' :
                              execution.status === 'failed' ? '#991b1b' :
                              execution.status === 'pending' ? '#92400e' :
                              '#374151',
                            border: 
                              execution.status === 'executed' ? '1px solid #10b981' :
                              execution.status === 'failed' ? '1px solid #ef4444' :
                              execution.status === 'pending' ? '1px solid #f59e0b' :
                              '1px solid #9ca3af',
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {execution.scheduled_at
                          ? new Date(execution.scheduled_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {execution.executed_at
                          ? new Date(execution.executed_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {execution.sms_delivered_at
                          ? new Date(execution.sms_delivered_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                backgroundColor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #3b82f6',
                '& .MuiAlert-icon': {
                  color: '#3b82f6',
                },
              }}
            >
              Nenhuma execução encontrada
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenHistoryDialog(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
